import {
  AttendanceStatus,
  DayOfWeek,
  GuardianRelationshipType,
  SchoolAnnouncementSeverity,
  TicketStatus,
  type Bill,
  type BillDetail,
  type GuardianChildOverview,
  type GuardianStudent,
  type School,
  type Ticket,
  type TicketCategory,
  type InitiatePaymentInput,
  type UpdateGuardianNotificationPreferencesMutationVariables,
} from '@/types/operations';
import { billStatusLabel, humanizeCode, isCode, normalizeCode } from '@/utils/billingLabels';
import { getStatusSeverity } from '@/utils/statusSeverity';
import { isBillPayable, payerBillBadge } from '@/composables/billing/studentBillSelection';

type TagSeverity = 'success' | 'warn' | 'danger' | 'info' | 'secondary';

/** How a guardian is related to a child, in the order parents expect. */
export const RELATIONSHIP_OPTIONS = [
  { label: 'Mother', value: GuardianRelationshipType.Mother },
  { label: 'Father', value: GuardianRelationshipType.Father },
  { label: 'Guardian', value: GuardianRelationshipType.Guardian },
  { label: 'Grandparent', value: GuardianRelationshipType.Grandparent },
  { label: 'Sponsor', value: GuardianRelationshipType.Sponsor },
  { label: 'Other', value: GuardianRelationshipType.Other },
];

/** Linking codes are shown and typed in capitals; spaces are never part of one. */
export const normaliseLinkingCode = (code: string): string => code.replace(/\s+/g, '').toUpperCase();

// ---------- Notification preferences ----------

export type NotificationPreferenceKey =
  | 'receivesFeeNotifications'
  | 'receivesAttendanceNotifications'
  | 'receivesResultNotifications';

/** One guardian-student link's saved preferences, keyed by the pivot id. */
export type NotificationPreferences = Pick<GuardianStudent, 'id' | NotificationPreferenceKey>;

export function preferencesFromLink(link: NotificationPreferences): NotificationPreferences {
  return {
    id: String(link.id),
    receivesFeeNotifications: !!link.receivesFeeNotifications,
    receivesAttendanceNotifications: !!link.receivesAttendanceNotifications,
    receivesResultNotifications: !!link.receivesResultNotifications,
  };
}

/**
 * Variables for one toggle: the pivot id plus ONLY the flag that changed, so
 * a save never overwrites the other two with stale client values.
 */
export function preferenceUpdateVariables(
  linkId: string,
  key: NotificationPreferenceKey,
  value: boolean,
): UpdateGuardianNotificationPreferencesMutationVariables {
  return { guardianStudentId: linkId, [key]: value };
}

// ---------- Child selection ----------

interface ChildRef { student: { id: string } }

/**
 * The child the portal should show: the stored choice when it is still one of
 * the guardian's children, otherwise the first child. Empty when there are none.
 */
export function resolveSelectedChildId(children: ReadonlyArray<ChildRef>, storedId: string | null | undefined): string {
  if (!children.length) return '';
  if (storedId && children.some(c => String(c.student.id) === String(storedId))) return String(storedId);
  return String(children[0]!.student.id);
}

// ---------- Result calendars ----------

interface CalendarRef { id: string; is_active?: boolean | null }

/**
 * Keeps the chosen calendar while it belongs to the list; otherwise falls back
 * to the active calendar, then the first one.
 */
export function pickCalendarId(calendars: ReadonlyArray<CalendarRef>, currentId: string | null | undefined): string {
  if (!calendars.length) return '';
  if (currentId && calendars.some(c => String(c.id) === String(currentId))) return String(currentId);
  const active = calendars.find(c => c.is_active);
  return String((active ?? calendars[0]!).id);
}

// ---------- Bill payment ----------

type PayableDetail = Pick<BillDetail, 'id' | 'balance'>;

/**
 * One payment line per fee that still has a balance. `balance` is the child's
 * own balance on the line (after discounts, exemptions and what was paid), so
 * a scholarship child is never asked for the full fee — the backend refuses
 * any line above the student's balance.
 */
export function outstandingPaymentLines(details: ReadonlyArray<PayableDetail>): NonNullable<InitiatePaymentInput['billDetailAndAmount']> {
  return details
    .map(d => ({ bill_detail_id: String(d.id), amount: Math.max(0, Number(d.balance ?? 0)) }))
    .filter(line => line.amount > 0);
}

/**
 * The payment for a child's bill. `studentId` must be the child the bill was
 * opened for (from the route), never whichever child the switcher shows now.
 */
export function buildGuardianPaymentInput(
  bill: { balance: number; billDetails: ReadonlyArray<PayableDetail> },
  studentId: string,
  processor: string,
  paymentMethod = '',
): InitiatePaymentInput | null {
  if (!studentId || !processor || bill.balance <= 0) return null;
  // Interswitch charges by method (card / transfer); the backend refuses without one.
  if (isInterswitch(processor) && !paymentMethod) return null;
  const billDetailAndAmount = outstandingPaymentLines(bill.billDetails);
  const amount = billDetailAndAmount.reduce((sum, line) => sum + line.amount, 0);
  if (amount <= 0) return null;
  return {
    amount,
    billDetailAndAmount,
    student_id: String(studentId),
    processor,
    ...(isInterswitch(processor) ? { paymentMethod } : {}),
  };
}

export const isInterswitch = (processor: string | null | undefined): boolean => normalizeCode(processor) === 'interswitch';

/** What the selected lines add up to: the base amount a gateway fee is added to. */
export const payableAmount = (details: ReadonlyArray<PayableDetail>): number =>
  outstandingPaymentLines(details).reduce((sum, line) => sum + line.amount, 0);

// ---------- Display helpers (parents never see raw codes or ids) ----------

interface ChildNameParts { first_name?: string | null; surname?: string | null }

export function childFullName(student: ChildNameParts | null | undefined): string {
  return [student?.first_name, student?.surname].filter(Boolean).join(' ');
}

export function childInitials(student: ChildNameParts | null | undefined): string {
  return ((student?.first_name?.[0] ?? '') + (student?.surname?.[0] ?? '')).toUpperCase() || '?';
}

type ChildPlacement = Partial<Pick<GuardianChildOverview, 'currentClassroom' | 'currentProgramme' | 'currentLevel'>> & {
  school?: Pick<School, 'school_type'> | null;
};

/** "Computer Science — 200 Level" for tertiary, else the class name. */
export function childPlacementLabel(child: ChildPlacement | null | undefined): string {
  if (!child) return '';
  if (normalizeCode(child.school?.school_type) === 'tertiary') {
    return [child.currentProgramme || 'No programme yet', child.currentLevel].filter(Boolean).join(' — ');
  }
  return child.currentClassroom || 'Not assigned to a class yet';
}

/** 'GRANDPARENT' → 'Grandparent'. */
export const relationshipLabel = (type: string | null | undefined): string => humanizeCode(type);

const ATTENDANCE_SEVERITY: Record<string, TagSeverity> = {
  [normalizeCode(AttendanceStatus.Present)]: 'success',
  [normalizeCode(AttendanceStatus.Absent)]: 'danger',
  [normalizeCode(AttendanceStatus.Late)]: 'warn',
  [normalizeCode(AttendanceStatus.Excused)]: 'info',
};

export const attendanceStatusSeverity = (status: string | null | undefined): TagSeverity =>
  ATTENDANCE_SEVERITY[normalizeCode(status)] ?? 'secondary';

const ANNOUNCEMENT_SEVERITY: Record<string, TagSeverity> = {
  [normalizeCode(SchoolAnnouncementSeverity.Urgent)]: 'danger',
  [normalizeCode(SchoolAnnouncementSeverity.Important)]: 'warn',
  [normalizeCode(SchoolAnnouncementSeverity.Info)]: 'info',
};

export const announcementSeverity = (severity: string | null | undefined): TagSeverity =>
  ANNOUNCEMENT_SEVERITY[normalizeCode(severity)] ?? 'info';

interface BillPeriod {
  session?: { name?: string | null } | null;
  academicCalendar?: { termStructure?: { name?: string | null } | null } | null;
}

/** "First Term — 2025/2026", or just the session; never the bill's database id. */
export function billPeriodLabel(bill: BillPeriod | null | undefined): string {
  const term = bill?.academicCalendar?.termStructure?.name ?? '';
  const session = bill?.session?.name ?? '';
  if (term && session) return `${term} — ${session}`;
  return term || session || 'School bill';
}

/** What the child still owes on a bill: the backend's per-student balance. */
export const billOutstanding = (bill: { balance?: number | null }): number => Math.max(0, Number(bill.balance ?? 0));

/** Share of the child's bill already paid, from what was paid and what is left. */
export function billPaidPercentage(bill: { amount_paid?: number | null; balance?: number | null }): number {
  const paid = Math.max(0, Number(bill.amount_paid ?? 0));
  const owed = billOutstanding(bill);
  if (paid + owed <= 0) return 100;
  return Math.min(100, Math.round((paid / (paid + owed)) * 100));
}

type BadgeBill = Pick<Bill, 'balance'> & Partial<Pick<Bill, 'status' | 'amount_paid' | 'due_date'>>;

/**
 * The badge a parent sees on a bill. A class or programme bill's `status` is
 * the shared bill's, so the child's own balance decides first (Paid, Part
 * paid, Overdue); otherwise the bill status in payer words.
 */
export function guardianBillBadge(bill: BadgeBill): { label: string; severity: TagSeverity } {
  const badge = payerBillBadge(bill);
  if (badge) return { label: badge.label, severity: badge.severity };
  return { label: billStatusLabel(bill.status, 'payer'), severity: getStatusSeverity(bill.status) as TagSeverity };
}

type OrderedBill = Pick<Bill, 'id' | 'balance'> & Partial<Pick<Bill, 'due_date' | 'created_at'>> & {
  billDetails: Array<Pick<BillDetail, 'id' | 'balance'>>;
};

/**
 * The older bill that must be paid before this one, or null. Same rule as the
 * student billing page (isBillPayable) and the backend's payment order check.
 */
export function olderBillToPayFirst<B extends OrderedBill>(bills: ReadonlyArray<B>, billId: string): B | null {
  const bill = bills.find(b => String(b.id) === String(billId));
  if (!bill || bill.balance <= 0 || isBillPayable(bill, [...bills], () => false)) return null;
  const key = bill.due_date || bill.created_at || '';
  return [...bills]
    .filter(other => other.id !== bill.id && other.balance > 0 && (other.due_date || other.created_at || '') < key)
    .sort((a, b) => (a.due_date || a.created_at || '').localeCompare(b.due_date || b.created_at || ''))[0] ?? null;
}

const WEEKDAYS = [DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday, DayOfWeek.Thursday, DayOfWeek.Friday];
const WEEKEND = [DayOfWeek.Saturday, DayOfWeek.Sunday];

/** Monday–Friday, plus a weekend day only when the timetable uses it. */
export function timetableDays(entries: ReadonlyArray<{ day_of_week?: string | null }>): DayOfWeek[] {
  const used = new Set(entries.map(e => normalizeCode(e.day_of_week)));
  return [...WEEKDAYS, ...WEEKEND.filter(day => used.has(normalizeCode(day)))];
}

/** Time slots in the order of the school day. */
export function sortTimeSlots<T extends { start_time?: string | null }>(slots: ReadonlyArray<T>): T[] {
  return [...slots].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
}

/** Locale date for parents: "Mon, 6 Oct 2025" (weekday optional). */
export function formatPortalDate(value: string | null | undefined, withWeekday = false): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-NG', {
    ...(withWeekday ? { weekday: 'short' as const } : {}),
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** "Just now", "5m ago", "3h ago", "2d ago", then a short date. */
export function formatRelativeTime(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-NG', { day: 'numeric', month: 'short' });
}

/**
 * A result summary (a JSON object of totals) as labelled rows. Nested values
 * and empty ones are skipped rather than printed as "[object Object]".
 */
export function resultSummaryEntries(summary: unknown): Array<{ key: string; label: string; value: string }> {
  if (!summary || typeof summary !== 'object' || Array.isArray(summary)) return [];
  return Object.entries(summary as Record<string, unknown>)
    .filter(([, value]) => value !== null && value !== undefined && value !== '' && typeof value !== 'object')
    .map(([key, value]) => ({ key, label: humanizeCode(key), value: String(value) }));
}

const ACTIVITY_ICONS: Record<string, string> = {
  result: 'pi-chart-bar',
  payment: 'pi-credit-card',
  attendance: 'pi-calendar',
  announcement: 'pi-megaphone',
  bill: 'pi-wallet',
};

/** PrimeIcon for a dashboard activity item's type. */
export const activityIcon = (type: string | null | undefined): string =>
  ACTIVITY_ICONS[normalizeCode(type)] ?? 'pi-info-circle';

// ---------- Support tickets ----------

export type TicketTab = 'active' | 'closed' | 'all';

type TicketRow = Partial<Pick<Ticket, 'status' | 'subject' | 'created_at'>> & {
  category?: Pick<TicketCategory, 'name'> | null;
};

const isFinished = (status: string | null | undefined) =>
  isCode(status, TicketStatus.Closed) || isCode(status, TicketStatus.Resolved);

/** The tab's tickets matching the search, newest first. */
export function filterTickets<T extends TicketRow>(tickets: ReadonlyArray<T>, tab: TicketTab, search: string): T[] {
  const query = search.trim().toLowerCase();
  return tickets
    .filter(t => (tab === 'all' ? true : tab === 'closed' ? isFinished(t.status) : !isFinished(t.status)))
    .filter(t => !query
      || (t.subject ?? '').toLowerCase().includes(query)
      || (t.category?.name ?? '').toLowerCase().includes(query))
    .sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime());
}
